/**
 * Utility functions for invoice item calculations
 */

import { LineItemSchema, LineItemTaxSchema } from "@billingnow/zod-schema";
import { round } from "lodash";

export interface InvoiceItemForCalculation {
  quantity: number;
  netPrice: number;
  grossPrice: number;
  discount?: number | null;
  discountType?: "percentage" | "fixed";
  taxes?: LineItemSchema["taxes"];
  showItemsWithTax: boolean;
}
export interface CalculateDiscountAmountProps {
  quantity: number;
  unitPrice: number;
  discount?: number | null;
  discountType?: "percentage" | "fixed";
}

/**
 * Calculate the discount amount for display purposes
 */
export function calculateDiscountAmount(item: CalculateDiscountAmountProps): number {
  const { quantity, unitPrice, discountType = "percentage" } = item;
  const discount = item.discount || 0;

  const itemtotal = quantity * unitPrice;

  if (discountType === "percentage") return (itemtotal * discount) / 100;
  else return discount;
}

/**
 * Build per-unit tax multipliers from the tax tree.
 * Returns: Map<taxId, perUnitMultiplier>
 */
function buildPerUnitMultipliers(taxes: LineItemTaxSchema[]): Map<string, number> {
  const map = new Map<string, number>();

  function resolve(tax: LineItemTaxSchema, taxes: LineItemTaxSchema[]): number {
    if (map.has(tax.id)) return map.get(tax.id)!;

    // tax applied on net only
    let baseMultiplier = 1;

    // if cumulative, add all children multipliers recursively
    if (tax.cumulativeTaxes?.length) {
      for (const child of tax.cumulativeTaxes) {
        if (taxes.find((t) => t.id === child.id)) baseMultiplier += resolve(child, taxes);
      }
    }

    const multiplier = (tax.taxRate / 100) * baseMultiplier;
    map.set(tax.id, multiplier);
    return multiplier;
  }

  for (const t of taxes) resolve(t, taxes);
  return map;
}

/**
 * Calculate all taxes for a line
 */
interface CalculateTaxesAmountProps {
  amount: number;
  quantity: number;
  isNetPrice: boolean;
  taxes?: LineItemTaxSchema[];
  discount?: number | null;
  discountType?: "percentage" | "fixed";
}
export function calculateTaxesAmount({
  amount,
  quantity,
  isNetPrice,
  taxes = [],
  discount,
  discountType,
}: CalculateTaxesAmountProps): number {
  if (taxes.length === 0) return 0;

  const discountValue = calculateDiscountAmount({
    quantity,
    unitPrice: amount,
    discount,
    discountType,
  });

  const baseNet = isNetPrice
    ? amount * quantity - discountValue
    : (() => {
        // gross → net
        const multipliers = buildPerUnitMultipliers(taxes);
        const gross = amount * quantity - discountValue;
        const grossPerUnit = 1 + Array.from(multipliers.values()).reduce((a, b) => a + b, 0);
        return gross / grossPerUnit;
      })();

  const multipliers = buildPerUnitMultipliers(taxes);

  let totalTax = 0;
  for (const t of taxes) {
    totalTax += baseNet * multipliers.get(t.id)!;
  }

  return round(totalTax, 2);
}

/**
 * Calculate all taxes for a line
 */
interface CalculateTaxableAmountProps {
  netPrice: number;
  quantity: number;
  tax: LineItemTaxSchema;
  taxes: LineItemTaxSchema[];
  discount?: number | null;
  discountType?: "percentage" | "fixed";
}
export function calculateTaxableAmount({
  netPrice,
  quantity,
  tax,
  taxes = [],
  discount,
  discountType,
}: CalculateTaxableAmountProps): number {
  const discountValue = calculateDiscountAmount({
    quantity,
    unitPrice: netPrice,
    discount,
    discountType,
  });

  const baseNet = netPrice * quantity - discountValue;

  const multipliers = buildPerUnitMultipliers(taxes);

  if (tax.taxRate === 0) return baseNet * multipliers.get(tax.id)!;

  return round((baseNet * multipliers.get(tax.id)!) / (tax.taxRate / 100), 2);
}

/**
 * Calculate one tax (optional helper)
 */
interface CalculateTaxAmountProps {
  amount: number;
  quantity?: number;
  isNetPrice: boolean;
  tax: LineItemTaxSchema;
  taxes?: LineItemTaxSchema[];
  discount?: number | null;
  discountType?: "percentage" | "fixed";
}
export function calculateTaxAmount({
  amount,
  quantity = 1,
  isNetPrice,
  tax,
  taxes = [],
  discount,
  discountType,
}: CalculateTaxAmountProps): number {
  const discountValue = calculateDiscountAmount({
    quantity,
    unitPrice: amount,
    discount,
    discountType,
  });

  const multipliers = buildPerUnitMultipliers(taxes);

  const baseNet = isNetPrice
    ? amount * quantity - discountValue
    : (() => {
        const gross = amount * quantity - discountValue;
        const grossPerUnit = 1 + Array.from(multipliers.values()).reduce((a, b) => a + b, 0);
        return gross / grossPerUnit;
      })();

  return round(baseNet * multipliers.get(tax.id)!, 2);
}

// Calculate net price from gross price and VAT rate
export function calculateNetPrice(grossPrice: number, taxes: LineItemSchema["taxes"]): number {
  return round(
    taxes?.length
      ? grossPrice - calculateTaxesAmount({ amount: grossPrice, quantity: 1, isNetPrice: false, taxes })
      : grossPrice,
    2,
  );
}

// Calculate gross price from net price and VAT rate
export function calculateGrossPrice(netPrice: number, taxes: LineItemSchema["taxes"]): number {
  return round(
    taxes?.length
      ? netPrice + calculateTaxesAmount({ amount: netPrice, quantity: 1, isNetPrice: true, taxes })
      : netPrice,
    2,
  );
}

/**
 * Calculate the item total including tax if applicable
 */
export function calculateItemTotalWithTax(item: InvoiceItemForCalculation): number {
  const { grossPrice, netPrice, quantity, showItemsWithTax, taxes } = item;

  if (showItemsWithTax)
    return round(
      grossPrice * quantity -
        calculateDiscountAmount({
          quantity,
          unitPrice: grossPrice,
          discount: item.discount,
          discountType: item.discountType,
        }),
      2,
    );

  return round(
    calculateGrossPrice(
      netPrice * quantity -
        calculateDiscountAmount({
          quantity,
          unitPrice: netPrice,
          discount: item.discount,
          discountType: item.discountType,
        }),
      taxes,
    ),
    2,
  );
}

/**
 * Calculate the item total excluding tax
 */
export function calculateItemTotalWithoutTax(item: InvoiceItemForCalculation): number {
  const { grossPrice, netPrice, quantity, showItemsWithTax, taxes } = item;

  if (showItemsWithTax)
    return round(
      calculateNetPrice(
        grossPrice * quantity -
          calculateDiscountAmount({
            quantity,
            unitPrice: grossPrice,
            discount: item.discount,
            discountType: item.discountType,
          }),
        taxes,
      ),
      2,
    );

  return round(
    netPrice * quantity -
      calculateDiscountAmount({
        quantity,
        unitPrice: netPrice,
        discount: item.discount,
        discountType: item.discountType,
      }),
    2,
  );
}

// Calculate subtotal value for all items
export const getSubTotalValue = (items: LineItemSchema[], showItemsWithTax: boolean) => {
  return round(
    items?.reduce((acc, item) => {
      const quantity = item.quantity || 0;
      const netPrice = item.netPrice || 0;
      const grossPrice = item.grossPrice || 0;
      const discount = item.discount || 0;
      const discountType = item.discountType || "percentage";

      return (
        acc +
        calculateItemTotalWithoutTax({
          quantity,
          netPrice,
          grossPrice,
          discount,
          discountType,
          taxes: item.taxes,
          showItemsWithTax,
        })
      );
    }, 0),
    2,
  );
};

// Calculate total Tax amount for all items
export const getTotalTaxValues = (items: LineItemSchema[]) => {
  const taxTotal: { tax: LineItemTaxSchema; total: number; taxableAmount: number }[] = [];

  items?.map((item) => {
    if (item.taxes && item.taxes.length > 0) {
      item.taxes.forEach((tax) => {
        const quantity = item.quantity || 0;
        const netPrice = item.netPrice || 0;
        const discount = item.discount || 0;
        const discountType = item.discountType || "percentage";
        const taxableAmount = calculateTaxableAmount({
          netPrice,
          quantity,
          tax,
          taxes: item.taxes || [],
          discount,
          discountType,
        });
        const taxValue = calculateTaxAmount({
          amount: item.netPrice,
          quantity: item.quantity,
          isNetPrice: true,
          tax,
          taxes: item.taxes as LineItemTaxSchema[],
          discount: item.discount,
          discountType: item.discountType,
        });

        const taxIndex = taxTotal.findIndex((t) => t.tax.id === tax.id);
        if (taxIndex > -1) {
          taxTotal[taxIndex]!.total += taxValue || 0;
          taxTotal[taxIndex]!.taxableAmount += taxableAmount;
        } else {
          taxTotal.push({
            tax,
            total: taxValue || 0,
            taxableAmount,
          });
        }
      });
    }
  });

  return taxTotal;
};

// Calculate total value including billing details adjustments
export const getTotalValue = ({ items, showItemsWithTax }: { items: LineItemSchema[]; showItemsWithTax: boolean }) => {
  return round(
    items?.reduce((acc, item) => {
      const quantity = item.quantity || 0;
      const grossPrice = item.grossPrice || 0;
      const netPrice = item.netPrice || 0;
      const discount = item.discount || 0;
      const discountType = item.discountType || "percentage";

      return (
        acc +
        calculateItemTotalWithTax({
          quantity,
          netPrice,
          grossPrice,
          discount,
          discountType,
          taxes: item.taxes,
          showItemsWithTax,
        })
      );
    }, 0),
    2,
  );
};
